// marketDerivedData.ts
import { excelSerialToJsDate, javascriptDateToMMDDYYYY, javascriptDateToYYYYMMDD } from '../helpers/date-helpers';
import { isSupportedDataType, isValidCurrency, isValidDateInput, isValidExchangeTickerSymbol } from '../helpers/args-validators';
import { FunctionCallCache } from '../helpers/functionCallCache';
import { FunctionLocationTracker } from '../helpers/functionLocationTracker';
import { KeyBuilder } from '../helpers/keyBuilder';
// Market Derived Data UI datatypes (Excel-facing)
export const SUPPORTED_DATETYPES = ['Period_High', 'Period_Low', 'Period_VWAP', 'Period_Average', 'Period_Median', 'Period_P25', 'Period_P75'];
function normalizeUiTypeToBackend(x) {
    // "Period High" / "Period_High" -> "period_high"
    return String(x ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_');
}
function parseDateArgToYYYYMMDD(date, fieldName) {
    if (date == null || String(date).trim() === '') {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, `${fieldName} is required (MM/DD/YYYY or Excel serial date).`);
    }
    const s = String(date).trim();
    // MM/DD/YYYY
    if (s.includes('/')) {
        if (!isValidDateInput(s)) {
            throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, `Invalid ${fieldName} (expected MM/DD/YYYY or Excel serial date).`);
        }
        const d = new Date(s);
        return javascriptDateToYYYYMMDD(d);
    }
    // Excel serial number
    const n = Number(date);
    if (Number.isFinite(n)) {
        const d = excelSerialToJsDate(n);
        return javascriptDateToYYYYMMDD(d);
    }
    // ISO-like
    const iso = s.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso))
        return iso;
    throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, `Invalid ${fieldName} (expected MM/DD/YYYY or Excel serial date).`);
}
function validateArgs(symbol, dataType, startDate, endDate, currency) {
    if (!isValidExchangeTickerSymbol(symbol)) {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, 'Invalid symbol (expected format: EXCHANGE:TICKER)');
    }
    if (!isSupportedDataType(SUPPORTED_DATETYPES, dataType)) {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, `Invalid data type. Supported: ${SUPPORTED_DATETYPES.join(', ')}`);
    }
    if (!isValidCurrency(currency)) {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, "Invalid currency (expected format: e.g., 'USD')");
    }
    const startISO = parseDateArgToYYYYMMDD(startDate, 'Start Date');
    const endISO = parseDateArgToYYYYMMDD(endDate, 'End Date');
    if (startISO > endISO) {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, `Invalid date range: Start Date (${startISO}) is after End Date (${endISO}).`);
    }
}
let delegate = () => {
    throw new CustomFunctions.Error(CustomFunctions.ErrorCode.notAvailable, 'Initializing… press F9 to recalc.');
};
export function setMarketDerivedDataDelegate(fn) {
    delegate = fn;
}
export function marketDerivedDataWrapper(symbol, dataType, startDate, endDate, currency, invocation) {
    return delegate(symbol, dataType, startDate, endDate, currency, invocation);
}
/**
 * Excel entrypoint implementation:
 * Alpha.Market_Derived_Data(symbol, dataType, startDate, endDate, currency)
 */
export function marketDerivedDataImpl(symbol, dataType, startDate, endDate, currency, invocation) {
    validateArgs(symbol, dataType, startDate, endDate, currency);
    const uiDataType = dataType;
    const startISO = parseDateArgToYYYYMMDD(startDate, 'Start Date');
    const endISO = parseDateArgToYYYYMMDD(endDate, 'End Date');
    const cache = FunctionCallCache.getInstance();
    const tracker = FunctionLocationTracker.getInstance();
    const args = {
        symbol: String(symbol).trim().toUpperCase(),
        dataType: normalizeUiTypeToBackend(uiDataType), // "Period_High" -> "period_high"
        startDate: startISO,
        endDate: endISO,
        currency: String(currency ?? '').trim().toUpperCase(),
    };
    const key = KeyBuilder.build('market_derived_data', args);
    cache.registerByKey(key, { functionName: 'market_derived_data', params: args });
    if (invocation?.address) {
        const jsStart = String(startDate).includes('/') ? new Date(startDate) : excelSerialToJsDate(Number(startDate));
        const jsEnd = String(endDate).includes('/') ? new Date(endDate) : excelSerialToJsDate(Number(endDate));
        tracker.registerCell(key, invocation.address, `=ALPHA.MARKET_DERIVED_DATA("${args.symbol}", "${uiDataType}", "${javascriptDateToMMDDYYYY(jsStart)}", "${javascriptDateToMMDDYYYY(jsEnd)}", "${args.currency}")`);
        cache.setAutoRefresh(key);
    }
    return cache.getValueForExcelByKey(key);
}
//# sourceMappingURL=marketDerivedData.js.map